"use client";

import { useCallback, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { capture } from "@/lib/analytics";
import { GUEST_DEMO_ID } from "@/lib/guestDemo";
import type { GuestProgressEvent } from "@/lib/guestProgress";

const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
};
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadTurnstile(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-turnstile="1"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile_load")));
      return;
    }
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.turnstile = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile_load"));
    document.head.appendChild(s);
  });
}

async function readGuestProgressStream(
  res: Response,
  onEvent: (event: GuestProgressEvent) => void,
): Promise<{ redirect?: string; errorMessage?: string }> {
  if (!res.body) {
    const data = (await res.json().catch(() => ({}))) as {
      redirect?: string;
      message?: string;
    };
    if (!res.ok) return { errorMessage: data.message || "Could not start a guest session." };
    return { redirect: data.redirect };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let redirect: string | undefined;
  let errorMessage: string | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      let event: GuestProgressEvent;
      try {
        event = JSON.parse(trimmed) as GuestProgressEvent;
      } catch {
        continue;
      }
      onEvent(event);
      if (event.type === "done") redirect = event.redirect;
      if (event.type === "error") errorMessage = event.message;
    }
  }

  if (buffer.trim()) {
    try {
      const event = JSON.parse(buffer.trim()) as GuestProgressEvent;
      onEvent(event);
      if (event.type === "done") redirect = event.redirect;
      if (event.type === "error") errorMessage = event.message;
    } catch {
      /* ignore trailing partial */
    }
  }

  return { redirect, errorMessage };
}

/**
 * "Start writing, no signup" — mints a guest session. Cloudflare Turnstile
 * (managed mode, invisible for humans) solves a challenge, then we POST the
 * token to /api/auth/guest, which verifies it server-side before creating the
 * guest row. Rendered only when guest mode + a site key are configured.
 */
export function GuestStartButton({
  label = "Try it now, no signup",
  variant = "primary",
}: {
  label?: string;
  variant?: "primary" | "secondary";
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("Verifying…");
  const [pct, setPct] = useState(4);
  const widgetRef = useRef<HTMLDivElement>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const submit = useCallback(
    async (token: string) => {
      try {
        setStatus("Setting up your guest account…");
        setPct(10);
        const res = await fetch("/api/auth/guest", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            turnstileToken: token,
            stream: true,
            // Cold visitors should land on the pre-scanned Continuity finding,
            // not an empty-feeling dashboard. Same target as /demo.
            demo: GUEST_DEMO_ID,
          }),
        });

        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as {
            message?: string;
          };
          setError(
            data.message ||
              "Could not start a guest session right now. Sign up free to continue.",
          );
          setBusy(false);
          return;
        }

        const contentType = res.headers.get("content-type") || "";
        if (contentType.includes("ndjson")) {
          const { redirect, errorMessage } = await readGuestProgressStream(
            res,
            (event) => {
              if (event.type === "progress") {
                setStatus(event.message);
                setPct(event.pct);
              } else if (event.type === "done") {
                setStatus("Opening Continuity Guardian…");
                setPct(100);
              }
            },
          );
          if (errorMessage) {
            setError(errorMessage);
            setBusy(false);
            return;
          }
          capture("guest_created");
          router.push(redirect || "/dashboard");
          router.refresh();
          return;
        }

        const data = (await res.json().catch(() => ({}))) as {
          redirect?: string;
          message?: string;
        };
        capture("guest_created");
        router.push(data.redirect || "/dashboard");
        router.refresh();
      } catch {
        setError("Something went wrong. Please try again.");
        setBusy(false);
      }
    },
    [router],
  );

  const start = useCallback(async () => {
    if (busy || !siteKey) return;
    setBusy(true);
    setError(null);
    setStatus("Verifying you are human…");
    setPct(6);
    try {
      await loadTurnstile();
      const el = widgetRef.current;
      if (!el || !window.turnstile) throw new Error("turnstile_unavailable");
      el.innerHTML = "";
      window.turnstile.render(el, {
        sitekey: siteKey,
        callback: (token: string) => submit(token),
        "error-callback": () => {
          setError("Verification failed. Please try again, or sign up free.");
          setBusy(false);
        },
        "expired-callback": () => setBusy(false),
      });
    } catch {
      setError("Could not load verification. Sign up free instead.");
      setBusy(false);
    }
  }, [busy, siteKey, submit]);

  if (!siteKey) return null;

  return (
    <div className="flex flex-col items-center gap-2 w-full max-w-sm mx-auto">
      {busy ? (
        <div className="w-full space-y-3 rounded-xl border border-border bg-card/80 px-5 py-4">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={status}
          >
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(4, Math.min(100, pct))}%` }}
            />
          </div>
          <p className="text-sm text-center text-muted-foreground">{status}</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={start}
          className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${variant === "secondary" ? "border border-border bg-background text-foreground hover:bg-muted" : "bg-accent text-accent-foreground hover:opacity-90"}`}
        >
          {label}
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
      {/* Turnstile renders here (invisible for most humans). */}
      <div ref={widgetRef} />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
